import React, {useEffect, useState} from 'react'
import {useUserContext} from "../contexts/UserContext";
import Button from "react-bootstrap/Button";
import Box from "@mui/material/Box";
import {useMediaQuery} from "react-responsive";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useDidMountEffect from "../hooks/useDidMountEffect";
import Stack from "@mui/material/Stack";
import classnames from "classnames";
import {LocationItem} from "./LocationItem";
import {CreateLocationInput} from "./CreateLocationInput";
import {mediaQueryForMobile} from "../pages/HomePage/HomePage";
import CircularProgress from "@mui/material/CircularProgress";
import {toast} from "react-toastify";
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import {buildLocationString} from "../utils/utils";
import Checkbox from "@mui/material/Checkbox";
import './LocationsTable.scss'

// The picker is a 760px modal, so its two columns stay comfortable far below
// the app-wide tablet breakpoint. Scoped here on purpose: widening
// mediaQueryForMobile would restyle unrelated pages.
export const mediaQueryForPickerStack = '(max-width: 640px)'

const normalizeZipcode = (zipcode) => String(zipcode ?? '').trim()

// By ZIP: the deal's current locations are flat fields with no saved-row id.
const isSameZipcode = (a, b) => normalizeZipcode(a?.zipcode) === normalizeZipcode(b?.zipcode)
const isLocationRow = (location, staged) => Boolean(normalizeZipcode(staged?.zipcode)) && isSameZipcode(location, staged)

// One half of a Settings section. The Stack props are identical for all four
// columns, so they live here instead of being repeated inline.
const ManagerColumn = ({ label, children }) => (
    <Stack
        className={'LocationsTable_section'}
        direction="column"
        sx={{ flex: 1, minWidth: 0, width: '100%' }}
    >
        <label>{label}</label>
        {children}
    </Stack>
)

// A Settings section: one heading over a left/right pair that collapses to a
// single column on narrow screens.
const ManagerSection = ({ title, isStacked, children }) => (
    <div className={'LocationsTable_manager-section'}>
        <div className={'LocationsTable_section-title'}>{title}</div>
        <Stack
            className={'LocationsTable_manager-columns'}
            direction={isStacked ? 'column' : 'row'}
            alignItems="start"
            spacing={isStacked ? 3 : 4}
        >
            {children}
        </Stack>
    </div>
)

export const LocationsTable = (props) => {
   const {
       selectLocation,
       onLocationCreate,
       isSaving,
       // Picker only: the record's current locations, which seed each side.
       customerLocation,
       dealerLocation,
       // Picker only: where "Change dealer location too" starts. Calculate
       // seeds it from its own card checkbox.
       initialShouldChangeDealerToo = true,
       // Default Settings manages the account's saved locations, so it keeps the
       // explicit Add step. Everywhere else the modal is a picker: Google's
       // verification saves and selects in one move.
       allowManualAdd = false,
       // Settings only: the account default as currently persisted, and the
       // modal-stack dismiss used by its header close button.
       defaultLocation,
       onCloseModal,
       // Overridable: Settings edits the account default, while the Deal and
       // Pricing flows edit a single record's location.
       title = 'Edit Location'
   } = props
    // Settings only: the saved row staged as the new default.
    const [selectedLocation, setSelectedLocation] = useState()
    // Picker only: each side's pick, applied together by Set Locations. The
    // modal remounts on every open, so the checkbox starts fresh each time.
    const [stagedCustomerLocation, setStagedCustomerLocation] = useState(customerLocation)
    const [stagedDealerLocation, setStagedDealerLocation] = useState(dealerLocation)
    const [shouldChangeDealerToo, setShouldChangeDealerToo] = useState(initialShouldChangeDealerToo)

    const { updateProfileResponse } = useUserContext()

    const [getLocationsResponse, getLocations] = useFetchHook(fetcher.getLocations)
    const [createLocationResponse, createLocation] = useFetchHook(fetcher.createLocation)
    // Its own hook so a create started from the Default section cannot put the
    // Saved section's button into a loading state, and vice versa.
    const [createDefaultLocationResponse, createDefaultLocation] = useFetchHook(fetcher.createLocation)
    // Same reason for the picker's Dealer field.
    const [createDealerLocationResponse, createDealerLocation] = useFetchHook(fetcher.createLocation)
    const [deleteLocationResponse, deleteLocation] = useFetchHook(fetcher.deleteLocation)
    const [locations, setLocations] = useState([])

    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const isNarrow = useMediaQuery({ query: mediaQueryForPickerStack })
    const isStacked = allowManualAdd ? isTabletOrMobile : isNarrow

    useEffect(() => {
        getLocations()
    }, [])

    useDidMountEffect(() => {
        getLocations()
    }, [deleteLocationResponse, createLocationResponse, createDefaultLocationResponse, createDealerLocationResponse, updateProfileResponse])

    useEffect(() => {
        if (getLocationsResponse?.data?.data) {
            setLocations(getLocationsResponse.data.data || [])
        }
    }, [getLocationsResponse])

    // Nothing server-side rejects a duplicate ZIP, so both sections look the
    // stored record up before they create anything.
    const findSavedLocation = (address) => {
        const zipcode = normalizeZipcode(address?.zipcode)
        return locations.find((l) => normalizeZipcode(l.zipcode) === zipcode)
    }

    // `stageLocation` and `create` are the picker side's; Settings passes neither.
    const onSaveLocation = (locationData, stageLocation, create = createLocation) => {
        // The picker saves on every verification, so the same ZIP arrives again
        // whenever it is re-entered — reuse the stored record rather than
        // growing the list.
        const existingLocation = findSavedLocation(locationData)
        if (existingLocation) {
            // Settings keeps Save and Set as Default apart, so a duplicate must
            // not move the pending default — it only reports there is nothing
            // to do. Silently selecting the row was the picker's behaviour.
            if (allowManualAdd) toast.warn("That ZIP is already saved.")
            else stageLocation(existingLocation)
            return
        }

        create(locationData).then((data) => {
            const newLocation = data?.data
            if (!allowManualAdd && newLocation) stageLocation(newLocation)
            onLocationCreate && onLocationCreate(data)
            toast.success("Successfully added location.")
        }).catch(() => {
            // Nothing was persisted, so nothing becomes selectable — without the
            // Add button there is no other signal that the save failed.
            toast.error("Could not save that location. Please try again.")
        })
    }

    // Settings only. The default is a foreign key, so a ZIP that is not stored
    // yet must be created before it can be applied — setting a default
    // necessarily saves the location. An already-selected saved row needs
    // neither the lookup nor the create.
    const onSetAsDefault = (address) => {
        if (!address) {
            if (selectedLocation) selectLocation(selectedLocation, true, true)
            return
        }

        const existingLocation = findSavedLocation(address)
        if (existingLocation) {
            selectLocation(existingLocation, true, true)
            return
        }

        createDefaultLocation(address).then((data) => {
            const newLocation = data?.data
            // Nothing to point the foreign key at if the create came back empty.
            if (!newLocation) return
            onLocationCreate && onLocationCreate(data)
            toast.success("Successfully added location.")
            selectLocation(newLocation, true, true)
        }).catch(() => {
            toast.error("Could not save that location. Please try again.")
        })
    }

    // Nothing staged means the persisted default stands. Selecting a row only
    // stages it — Set as Default is what writes it.
    const pendingDefaultLocation = selectedLocation || defaultLocation

    // One saved-location collection; the picker draws it once per side, each
    // with its own selection, so a delete always shows on both.
    const renderSavedLocationsList = (isSelected, onSelect) => (
        <div className={'LocationsTable_list'}>
            {
                locations.map((location) =>
                    <LocationItem
                        key={location.id}
                        location={location}
                        isSelected={isSelected(location)}
                        setSelectedLocation={onSelect}
                        deleteLocation={
                            (location) => deleteLocation(location).then(() => {
                                toast.success(`Successfully deleted location.`)
                            })
                        }
                        isInitialLocation={false}
                        selectOnTextClick={!allowManualAdd}
                    />
                )
            }
        </div>
    )

    const header = (
        <div className={'LocationsTable_header'}>
            <LocationOnOutlinedIcon/>
            <div>{title}</div>
            {
                // Settings is a manager, not a chooser: it must be dismissable
                // without applying a default. BaseModal renders no close of its
                // own, so the affordance lives here.
                allowManualAdd && onCloseModal &&
                <button
                    type="button"
                    className={'LocationsTable_close'}
                    onClick={onCloseModal}
                    aria-label={'Close'}
                >
                    <CloseIcon/>
                </button>
            }
        </div>
    )

    if (allowManualAdd) {
        return (
            <Box className={'LocationsTable LocationsTable--manager'}>
                {header}

                <div className={'LocationsTable_manager-body'}>
                    <ManagerSection title={'Default Location'} isStacked={isStacked}>
                        <ManagerColumn label={'Enter 5-digit zipcode'}>
                            <CreateLocationInput
                                isSaving={createDefaultLocationResponse?.loading}
                                onSave={onSetAsDefault}
                                allowManualAdd={true}
                                actionLabel={'Set as Default'}
                                showAddIcon={false}
                                // A staged saved row is already persisted, so it
                                // needs no freshly resolved address to apply.
                                allowActionWithoutAddress={Boolean(selectedLocation)}
                            />
                        </ManagerColumn>

                        <ManagerColumn label={'Current Default Location'}>
                            <div className={'LocationsTable_current-default'}>
                                <LocationOnOutlinedIcon/>
                                <span>{buildLocationString(pendingDefaultLocation) || '—'}</span>
                            </div>
                        </ManagerColumn>
                    </ManagerSection>

                    <ManagerSection title={'Saved Locations'} isStacked={isStacked}>
                        <ManagerColumn label={'Enter 5-digit zipcode'}>
                            <CreateLocationInput
                                isSaving={createLocationResponse?.loading}
                                onSave={onSaveLocation}
                                allowManualAdd={true}
                                actionLabel={'Save Location'}
                            />
                        </ManagerColumn>

                        <ManagerColumn label={'Saved Locations'}>
                            {renderSavedLocationsList(
                                (location) => location.id === selectedLocation?.id,
                                setSelectedLocation,
                            )}
                        </ManagerColumn>
                    </ManagerSection>
                </div>
            </Box>
        )
    }

    // Picker: Customer | Dealer, each with its own ZIP field and saved list.
    // A pick only stages its own side, except a Customer pick (ZIP or saved
    // row) while the checkbox is checked, which stages the Dealer too.
    const stageCustomerLocation = (location) => {
        setStagedCustomerLocation(location)
        if (shouldChangeDealerToo) setStagedDealerLocation(location)
    }

    // Unchanged sides are left out, so an untouched Customer never re-prompts
    // for a tax region.
    const isCustomerChanged = !isSameZipcode(stagedCustomerLocation, customerLocation)
    const isDealerChanged = !isSameZipcode(stagedDealerLocation, dealerLocation)
    const canSetLocations = isCustomerChanged || isDealerChanged

    // Checking the box brings Dealer in line with Customer at once; unchecking
    // only stops future syncing.
    const toggleChangeDealerToo = () => {
        const isChecking = !shouldChangeDealerToo
        setShouldChangeDealerToo(isChecking)
        if (isChecking && stagedCustomerLocation) setStagedDealerLocation(stagedCustomerLocation)
    }

    // One call for both sides: the callers merge into state from a closure, so
    // a second call would overwrite the first.
    const applyStagedLocations = () => selectLocation(
        isCustomerChanged ? stagedCustomerLocation : stagedDealerLocation,
        isCustomerChanged,
        isDealerChanged,
        stagedDealerLocation,
    )

    const changeDealerTooCheckbox = (
        <label className={'LocationsTable_toggle'}>
            <Checkbox
                checked={shouldChangeDealerToo}
                onChange={toggleChangeDealerToo}
            />
            <span className={'LocationsTable_toggle-label'}>Change dealer location too</span>
        </label>
    )

    const pickerSide = ({title, titleAction, location, isChanged, onZipSaved, createSideLocation, createResponse, onRowSelected}) => (
        <div className={classnames('LocationsTable_side', {changed: isChanged})}>
            <div className={'LocationsTable_side-title'}>
                <span>{title}</span>
                {titleAction}
            </div>
            <label>Enter 5-digit zipcode</label>
            <CreateLocationInput
                isSaving={createResponse?.loading}
                onSave={(locationData) => onSaveLocation(locationData, onZipSaved, createSideLocation)}
                location={location || null}
            />
            <label>Saved Locations</label>
            {renderSavedLocationsList((row) => isLocationRow(row, location), onRowSelected)}
        </div>
    )

    return (
        <Box className={'LocationsTable LocationsTable--picker'}>
            {header}

            <div className={'LocationsTable_body'}>
                <Stack
                    className={classnames('LocationsTable_sides', {stacked: isStacked})}
                    direction={isStacked ? 'column' : 'row'}
                >
                    {pickerSide({
                        title: 'Customer',
                        titleAction: changeDealerTooCheckbox,
                        location: stagedCustomerLocation,
                        isChanged: isCustomerChanged,
                        onZipSaved: stageCustomerLocation,
                        createSideLocation: createLocation,
                        createResponse: createLocationResponse,
                        onRowSelected: stageCustomerLocation,
                    })}
                    {pickerSide({
                        title: 'Dealer',
                        location: stagedDealerLocation,
                        isChanged: isDealerChanged,
                        onZipSaved: setStagedDealerLocation,
                        createSideLocation: createDealerLocation,
                        createResponse: createDealerLocationResponse,
                        onRowSelected: setStagedDealerLocation,
                    })}
                </Stack>
            </div>

            <div className={'LocationsTable_footer'}>
                <Button
                    className={classnames('LocationsTable_submit', {disabled: !canSetLocations})}
                    onClick={applyStagedLocations}
                    disabled={!canSetLocations}
                >
                    {isSaving ? <CircularProgress size={16} color="inherit"/> : 'Set Locations'}
                </Button>
            </div>
        </Box>
    )
}
